import React, {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Form, Input } from "rsuite";
import Button from "../../common/Button";
import {
  applyEndpointSelect,
  applyHourlyRentalMode,
  applyRegularTransferMode,
  buildMapMarkers,
  canSubmitForm,
  carClassFromNowTier,
  createWaypoint,
  getOrderSubmitLabel,
  nowTierFromCarClass,
} from "../orderFormConfig";
import { clampDurationHours, isAirportCoords, MAX_WAYPOINTS } from "../orderFormConstants";
import { DEFAULT_ORDER_FORM_LOCALE } from "../orderFormLocale";
import {
  clearMapDropoff,
  clearMapPickup,
  clearMapWaypoint,
  createMapCoordsCache,
  getMapCoordsForDisplay,
  rememberMapCoords,
} from "../mapDisplayCoords";
import { getWaypointIdFromMarker, toWaypointMarkerId } from "../routeMarkers";
import { isValidPhoneNumber } from "../contactValidation";
import { applyElitVehicleId, getElitDurationVehicle } from "../elitVehicleSelection";
import { canEstimateRidePrice, getRideOrderApiErrorMessage } from "../rideOrderPayload";
import NetworkService from "../../../NetworkService";
import { filterFavoriteAddressesByUserId } from "../favoriteAddresses";
import {
  FavoriteAddress,
  GeocodeSuggestion,
  MapMarkerId,
  OrderFormLocale,
  OrderFormMapState,
  OrderFormState,
  RideType,
  RouteCoords,
  RouteWaypoint,
  isScheduleRide,
  normalizeReverseAddress,
} from "../types";
import RouteInputs from "./RouteInputs";
import ContactFields from "./ContactFields";
import ScheduleFields from "./ScheduleFields";
import SegmentedToggle from "./SegmentedToggle";
import DurationStepper from "./DurationStepper";
import OrderLabeledTextarea from "./OrderLabeledTextarea";
import ServiceTierPicker from "./ServiceTierPicker";
import ElitVehicleSelect from "./ElitVehicleSelect";
import PassengerSelect from "./PassengerSelect";
import OrderPriceEstimate from "./OrderPriceEstimate";
import { useOrderPriceEstimate } from "./useOrderPriceEstimate";
import Checkbox from "../../common/Checkbox";
import { ORDER_FORM_FIELD_PROPS } from "./orderFormRsuite";
import styles from "./orderForm.module.scss";

type OrderFormProps = {
  locale?: OrderFormLocale;
  form: OrderFormState;
  favoriteAddresses?: FavoriteAddress[];
  companyAdminId?: number;
  onFormChange: (updates: Partial<OrderFormState> | ((prev: OrderFormState) => OrderFormState)) => void;
  onMapStateChange: (state: OrderFormMapState) => void;
  onScrollToMap?: () => void;
  getMapCenter: () => RouteCoords;
  onSubmit: (form: OrderFormState) => Promise<void>;
  isSubmitting?: boolean;
  isElitVehiclePanelOpen?: boolean;
  onElitVehiclePanelOpenChange?: (open: boolean) => void;
  rideType: RideType;
  onRideTypeChange?: (rideType: RideType) => void;
};

type MarkerPatch = {
  coords?: RouteCoords | null;
  address?: string;
};

const OrderForm: React.FC<OrderFormProps> = ({
  locale: localeProp,
  form,
  favoriteAddresses = [],
  companyAdminId,
  onFormChange,
  onMapStateChange,
  onScrollToMap,
  getMapCenter,
  onSubmit,
  isSubmitting = false,
  isElitVehiclePanelOpen = false,
  onElitVehiclePanelOpenChange,
  rideType,
  onRideTypeChange,
}) => {
  const locale = localeProp ?? DEFAULT_ORDER_FORM_LOCALE;
  const mapCoordsCacheRef = useRef(createMapCoordsCache());
  const userFavoriteAddresses = useMemo(
    () => filterFavoriteAddressesByUserId(favoriteAddresses, form.userId),
    [favoriteAddresses, form.userId]
  );
  const [contactTouched, setContactTouched] = useState({ phone: false });
  const [submitError, setSubmitError] = useState<string | null>(null);

  const selectedVehicle = useMemo(() => getElitDurationVehicle(form.vehicleId), [form.vehicleId]);
  const isTransferMode = form.transferMode;

  const phoneError =
    contactTouched.phone && form.phone.trim() && !isValidPhoneNumber(form.phone)
      ? locale.contact.invalidPhone
      : undefined;

  const priceEstimate = useOrderPriceEstimate({
    form,
    rideType,
    companyAdminId,
    locale,
  });

  const hasValidPriceEstimate =
    isScheduleRide(rideType) ||
    !canEstimateRidePrice(form, rideType) ||
    priceEstimate.status === "ready";

  const canSubmit =
    canSubmitForm(form, rideType) &&
    !phoneError &&
    !isSubmitting &&
    hasValidPriceEstimate;

  const handlePassengerSelect = useCallback(
    (passengerUserId: number) => {
      onFormChange((prev) => ({
        ...prev,
        userId: passengerUserId,
      }));
    },
    [onFormChange]
  );

  const handlePassengerClear = useCallback(() => {
    onFormChange((prev) => ({
      ...prev,
      userId: null,
    }));
  }, [onFormChange]);

  useEffect(() => {
    setContactTouched({ phone: false });
    setSubmitError(null);
    mapCoordsCacheRef.current = createMapCoordsCache();
  }, [rideType]);

  const update = <K extends keyof OrderFormState>(key: K, value: OrderFormState[K]) => {
    onFormChange((prev) => ({ ...prev, [key]: value }));
  };

  const mapWaypoints = useCallback(
    (fn: (waypoints: RouteWaypoint[]) => RouteWaypoint[]) => {
      onFormChange((prev) => ({ ...prev, waypoints: fn(prev.waypoints) }));
    },
    [onFormChange]
  );

  const updateWaypoint = useCallback(
    (waypointId: string, patch: Partial<RouteWaypoint>) => {
      mapWaypoints((waypoints) =>
        waypoints.map((waypoint) =>
          waypoint.id === waypointId ? { ...waypoint, ...patch } : waypoint
        )
      );
    },
    [mapWaypoints]
  );

  const patchMarker = useCallback(
    (markerId: MapMarkerId, patch: MarkerPatch) => {
      onFormChange((prev) => {
        if (markerId === "pickup") {
          return {
            ...prev,
            ...(patch.coords !== undefined ? { pickupCoords: patch.coords } : {}),
            ...(patch.address !== undefined ? { pickup: patch.address } : {}),
          };
        }

        if (markerId === "dropoff") {
          return {
            ...prev,
            ...(patch.coords !== undefined ? { dropoffCoords: patch.coords } : {}),
            ...(patch.address !== undefined ? { dropoff: patch.address } : {}),
          };
        }

        const waypointId = getWaypointIdFromMarker(markerId);
        if (!waypointId) return prev;

        return {
          ...prev,
          waypoints: prev.waypoints.map((waypoint) =>
            waypoint.id === waypointId ? { ...waypoint, ...patch } : waypoint
          ),
        };
      });
    },
    [onFormChange]
  );

  const handleMarkerDrag = useCallback(
    async (markerId: MapMarkerId, coords: RouteCoords) => {
      patchMarker(markerId, { coords });

      if (isAirportCoords(coords)) {
        patchMarker(markerId, { address: locale.schedule.airportAddress });
        return;
      }

      try {
        const result = await NetworkService.reverseGeocode(coords.lat, coords.lng);
        patchMarker(markerId, { address: normalizeReverseAddress(result) });
      } catch {
        // Keep coords; address text unchanged on failure
      }
    },
    [locale.schedule.airportAddress, patchMarker]
  );

  const placeMarkerOnMap = useCallback(
    (markerId: MapMarkerId) => {
      void handleMarkerDrag(markerId, getMapCenter());
      onScrollToMap?.();
    },
    [getMapCenter, handleMarkerDrag, onScrollToMap]
  );

  useEffect(() => {
    rememberMapCoords(mapCoordsCacheRef.current, {
      pickup: form.pickupCoords,
      dropoff: form.dropoffCoords,
      waypoints: form.waypoints,
    });

    const mapCoords = getMapCoordsForDisplay(mapCoordsCacheRef.current, {
      pickup: form.pickupCoords,
      dropoff: form.dropoffCoords,
      waypoints: form.waypoints,
    });

    onMapStateChange({
      markers: buildMapMarkers(
        form.pickup,
        form.dropoff,
        mapCoords.pickup,
        mapCoords.dropoff,
        mapCoords.waypoints
      ),
      onMarkerDrag: handleMarkerDrag,
    });
  }, [
    form.pickup,
    form.dropoff,
    form.pickupCoords,
    form.dropoffCoords,
    form.waypoints,
    handleMarkerDrag,
    onMapStateChange,
  ]);

  const handlePickupSelect = (suggestion: GeocodeSuggestion) => {
    onFormChange((prev) => applyEndpointSelect(prev, "pickup", suggestion));
  };

  const handleDropoffSelect = (suggestion: GeocodeSuggestion) => {
    onFormChange((prev) => applyEndpointSelect(prev, "dropoff", suggestion));
  };

  const handleTransferModeChange = (enabled: boolean) => {
    onFormChange((prev) => {
      if (!enabled) return applyHourlyRentalMode(prev);
      return applyRegularTransferMode(prev);
    });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;

    setSubmitError(null);
    try {
      await onSubmit(form);
    } catch (error) {
      setSubmitError(getRideOrderApiErrorMessage(error));
    }
  };

  const rideTypeLabels = locale.rideType ?? DEFAULT_ORDER_FORM_LOCALE.rideType;

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      <header className={styles.formHeader}>
        <h2 className={styles.formHeaderTitle}>{locale.headerTitle}</h2>
      </header>

      <PassengerSelect
        locale={locale}
        userId={form.userId}
        onUserSelect={handlePassengerSelect}
        onClear={handlePassengerClear}
      />

      {onRideTypeChange && (
        <SegmentedToggle
          className={styles.rideTypeToggle}
          labels={{
            legend: rideTypeLabels.label,
            firstOption: rideTypeLabels.orderNow,
            secondOption: rideTypeLabels.schedule,
            firstDescription: rideTypeLabels.orderNowDescription,
            secondDescription: rideTypeLabels.scheduleDescription,
          }}
          checked={rideType === "now"}
          onChange={(isNow) => onRideTypeChange(isNow ? "now" : "schedule")}
        />
      )}

      {isScheduleRide(rideType) && (
        <ElitVehicleSelect
          vehicleId={form.vehicleId}
          locale={locale}
          isPanelOpen={isElitVehiclePanelOpen}
          onVehicleChange={(vehicleId) =>
            onFormChange((prev) => applyElitVehicleId(prev, vehicleId))
          }
          onOpenPanel={() => onElitVehiclePanelOpenChange?.(true)}
        />
      )}

      {!isScheduleRide(rideType) && (
        <ServiceTierPicker
          selected={nowTierFromCarClass(form.carClass ?? 2)}
          onChange={(tier) => update("carClass", carClassFromNowTier(tier))}
        />
      )}

      <RouteInputs
        locale={locale}
        pickup={form.pickup}
        dropoff={form.dropoff}
        waypoints={form.waypoints}
        maxWaypoints={MAX_WAYPOINTS}
        favoriteAddresses={userFavoriteAddresses}
        onPickupChange={(value) => update("pickup", value)}
        onDropoffChange={(value) => update("dropoff", value)}
        onPickupSelect={handlePickupSelect}
        onDropoffSelect={handleDropoffSelect}
        onPickupCoordsClear={() => {
          clearMapPickup(mapCoordsCacheRef.current);
          update("pickupCoords", null);
        }}
        onDropoffCoordsClear={() => {
          clearMapDropoff(mapCoordsCacheRef.current);
          update("dropoffCoords", null);
        }}
        onPickupAddOnMap={() => placeMarkerOnMap("pickup")}
        onDropoffAddOnMap={() => placeMarkerOnMap("dropoff")}
        onWaypointChange={(waypointId, value) => updateWaypoint(waypointId, { address: value })}
        onWaypointSelect={(waypointId, suggestion) =>
          updateWaypoint(waypointId, {
            address: suggestion.formattedAddress,
            coords: { lat: suggestion.lat, lng: suggestion.lng },
          })
        }
        onWaypointCoordsClear={(waypointId) => {
          clearMapWaypoint(mapCoordsCacheRef.current, waypointId);
          updateWaypoint(waypointId, { coords: null });
        }}
        onWaypointAddOnMap={(waypointId) => placeMarkerOnMap(toWaypointMarkerId(waypointId))}
        onWaypointRemove={(waypointId) => {
          clearMapWaypoint(mapCoordsCacheRef.current, waypointId);
          mapWaypoints((waypoints) => waypoints.filter((waypoint) => waypoint.id !== waypointId));
        }}
        onAddWaypoint={() => mapWaypoints((waypoints) => [...waypoints, createWaypoint()])}
        onSwap={() =>
          onFormChange((prev) => ({
            ...prev,
            pickup: prev.dropoff,
            dropoff: prev.pickup,
            pickupCoords: prev.dropoffCoords,
            dropoffCoords: prev.pickupCoords,
          }))
        }
      />

      <ContactFields
        locale={locale}
        name={form.name}
        phoneDialCode={form.phoneDialCode}
        phone={form.phone}
        phoneError={phoneError}
        onNameChange={(value) => update("name", value)}
        onPhoneDialCodeChange={(value) => update("phoneDialCode", value)}
        onPhoneChange={(value) => update("phone", value)}
        onPhoneBlur={() => setContactTouched((prev) => ({ ...prev, phone: true }))}
      />

      {isScheduleRide(rideType) && (
        <ScheduleFields
          locale={locale}
          date={form.date}
          time={form.time}
          passengers={form.passengers}
          luggage={form.luggage}
          onDateChange={(value) => update("date", value)}
          onTimeChange={(value) => update("time", value)}
          onPassengersChange={(value) => update("passengers", value)}
          onLuggageChange={(value) => update("luggage", value)}
        />
      )}

      {isScheduleRide(rideType) && (
        <SegmentedToggle
          locale={locale}
          checked={form.transferMode}
          onChange={handleTransferModeChange}
        />
      )}

      {isScheduleRide(rideType) && (
        <DurationStepper
          key={form.vehicleId}
          locale={locale}
          value={isTransferMode ? 0 : form.durationHours}
          minHours={selectedVehicle.minHours}
          disabled={isTransferMode}
          onChange={(value) =>
            update("durationHours", clampDurationHours(value, selectedVehicle.minHours))
          }
        />
      )}

      <button
        type="button"
        className={styles.optionCheckboxRow}
        onClick={() => update("childSeat", !form.childSeat)}
        aria-pressed={form.childSeat}
      >
        <Checkbox selected={form.childSeat} onChange={() => undefined} className="" />
        <span className={styles.optionCheckboxLabel}>{locale.childSeatLabel}</span>
      </button>

      {isScheduleRide(rideType) && (
        <Form.Group controlId="schedule-flight-number" className={styles.schedFlightField}>
          <Form.ControlLabel className={styles.schedLabel}>
            {locale.schedule.flightNumberLabel}
          </Form.ControlLabel>
          <Input
            {...ORDER_FORM_FIELD_PROPS}
            className={styles.rsControl}
            value={form.flightNumber}
            onChange={(value) => update("flightNumber", value)}
            placeholder={locale.schedule.flightNumberPlaceholder}
            autoComplete="off"
          />
        </Form.Group>
      )}

      {isScheduleRide(rideType) && (
        <OrderLabeledTextarea
          controlId="ride-comment"
          label={locale.comment.label}
          value={form.comment}
          onChange={(value) => update("comment", value)}
          placeholder={locale.comment.placeholder}
        />
      )}

      <div className={styles.submitArea}>
        <OrderPriceEstimate
          form={form}
          rideType={rideType}
          estimate={priceEstimate}
          locale={locale}
        />
        {submitError && <p className={styles.submitError}>{submitError}</p>}
        <Button
          variant="green"
          className={styles.submitBtn}
          disabled={!canSubmit}
          type="submit"
        >
          {isSubmitting ? locale.submitting : getOrderSubmitLabel(rideType)}
        </Button>
      </div>
    </form>
  );
};

export default OrderForm;
